/**
 * A one-of-N choice with a search box in the menu, and room for a value the list
 * has never heard of.
 *
 * The third dropdown in this vocabulary, and it exists because the first two
 * each answer half of what a long list needs:
 *
 *  - `MenuField` is the app's own menu, but it lists everything at once. Correct
 *    for four transcription providers, useless for an ElevenLabs workspace of a
 *    hundred voices - which is exactly why ElevenLabs' own picker leads with
 *    "start typing to search".
 *  - `ComboField` types and filters, but its value IS its text, so it can only
 *    handle a list where the two are the same word. A voice is an opaque id with
 *    a person's name on it, and typing "Bella" into a field that stores
 *    `EXAVITQu4vr4xnSDxMaL` would store the wrong thing.
 *
 * So: a `MenuField` trigger showing the chosen option's LABEL, over a panel whose
 * first row is a filter. Values and labels stay separate, and the list can be a
 * hundred rows long without the panel becoming a scroll hunt.
 *
 * `allowCustom` is the half a `<select>` cannot do and is not decoration either:
 * a model id is the operator's field, this build's list of known ids goes stale
 * the week a provider renames one, and a settings page that refuses an id its
 * own version has not heard of is a settings page that needs a release to stay
 * usable. So the suggestions are suggestions, and anything typed is a value.
 *
 * Grouping is by `group` on the option rather than by nesting arrays: the two
 * synthesis services are one flat list that happens to have headings in it, and
 * a nested shape would make every caller that just wants "the chosen option"
 * walk two levels to find it.
 *
 * Must be rendered inside a `PopHost` - `Pop` reads its open slot from there, and
 * a `Pop` without one is a control that silently never opens.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Check, ChevronDown, CornerDownLeft, Search } from "lucide-react";
import { cn } from "@wa/ui";
import { Pop, useClosePops } from "../shell/Pop";

export interface PickerOption {
  value: string;
  label: string;
  /** A second line under the label - the raw id, a language, a model's reach. */
  hint?: string;
  /** Heading this option sits under. Options with no group come first, ungrouped. */
  group?: string;
  icon?: ReactNode;
}

/**
 * Keys the caret owns while the filter has focus.
 *
 * `Pop` listens for these on the panel to walk its rows, which is right for a
 * menu of buttons and wrong the moment one of the rows is a text input: End
 * would jump to the last voice instead of to the end of the word being typed.
 * Up and Down are deliberately NOT in here - stepping from the field into the
 * results is exactly what they should do.
 */
const CARET_KEYS = new Set(["Home", "End", "ArrowLeft", "ArrowRight"]);

/** Case- and diacritic-tolerant enough for Estonian names and model ids. */
function matches(option: PickerOption, needle: string): boolean {
  if (!needle) return true;
  const hay = `${option.label} ${option.hint ?? ""} ${option.value}`.toLocaleLowerCase("et");
  return hay.includes(needle);
}

export function PickerField({
  value,
  onChange,
  options,
  label,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  customLabel,
  allowCustom,
  mono,
  variant = "form",
  disabled,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: PickerOption[];
  /** Accessible name - the trigger only shows the current option. */
  label: string;
  /**
   * Two dresses, one control, exactly as `MenuField` has them: `form` is the
   * sunken 52px field that fills its column inside a dialog, `toolbar` is the
   * raised 44px pill that sits among other filters on a page surface.
   *
   * It defaults to `form` and NOT to `toolbar` the way `MenuField` does. That
   * asymmetry is deliberate: this control was born in the telephony settings
   * dialogs and every caller it had was a form field, so a `toolbar` default
   * would have restyled five live screens to buy consistency in a prop name
   * nobody reads. New callers say which one they are.
   *
   * The panel follows the dress. A form field is as wide as its column, so its
   * menu matches that width and hangs under the left edge; a toolbar pill is
   * small and its menu is wider than it, so it keeps its own width and grows
   * leftwards to stay inside the pane.
   */
  variant?: "toolbar" | "form";
  /** Shown on the trigger when nothing is chosen. */
  placeholder?: string;
  searchPlaceholder?: string;
  /** Shown when the filter matches nothing and `allowCustom` is off. */
  emptyLabel?: string;
  /** The row that takes whatever was typed. Needs `{value}`, e.g. `Use "{value}"`. */
  customLabel?: string;
  /** Let a typed value that matches no option be chosen anyway. */
  allowCustom?: boolean;
  /** For ids and model names, which read as one string of characters, not words. */
  mono?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const current = options.find((o) => o.value === value);
  const isForm = variant === "form";

  return (
    <Pop
      matchWidth={isForm}
      trigger={(open) => (
        <button
          type="button"
          className={cn(
            "crm-fld crm-fld-sel",
            isForm ? "crm-fld-form" : "w-auto min-w-0 gap-2.5 px-4 text-[12.75px]",
            // A toolbar pill also lights up while it HOLDS something, the way
            // `FilterMenu` does with its count: a filter behind a closed
            // dropdown is invisible, and a narrowed list and an empty one read
            // the same. A form field has no such state to advertise - a value
            // in a dialog is the normal condition, not a narrowing.
            (open || (!isForm && !!value)) && "is-on",
            disabled && "pointer-events-none opacity-55",
            className,
          )}
          aria-label={label}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
        >
          {current?.icon}
          <span
            className={cn(
              "crm-trunc flex-1 text-left",
              // A value the list does not know is still the value, and showing
              // it raw is the honest answer - a picker that fell back to the
              // placeholder would look empty while holding a saved model id.
              mono && "font-mono text-[12.25px]",
              !current && !value && "text-muted-foreground",
            )}
          >
            {current?.label ?? value ?? ""}
            {!current && !value ? placeholder : null}
          </span>
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>
      )}
    >
      <PickerPanel
        value={value}
        onChange={onChange}
        options={options}
        searchPlaceholder={searchPlaceholder ?? label}
        emptyLabel={emptyLabel}
        customLabel={customLabel}
        allowCustom={allowCustom}
        mono={mono}
      />
    </Pop>
  );
}

/** Mounted only while the panel is open, so the filter starts empty every time
 *  rather than reopening on the last search somebody ran. */
function PickerPanel({
  value,
  onChange,
  options,
  searchPlaceholder,
  emptyLabel,
  customLabel,
  allowCustom,
  mono,
}: {
  value: string;
  onChange: (v: string) => void;
  options: PickerOption[];
  searchPlaceholder: string;
  emptyLabel?: string;
  customLabel?: string;
  allowCustom?: boolean;
  mono?: boolean;
}) {
  const close = useClosePops();
  const [q, setQ] = useState("");

  const shown = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("et");
    return options.filter((o) => matches(o, needle));
  }, [options, q]);

  // What was typed, when it is a value in its own right rather than one of the
  // rows below. Offered as an explicit row instead of committed on blur: a
  // half-typed model id must not become the saved one because the operator
  // clicked elsewhere.
  const typed = q.trim();
  const custom =
    allowCustom && typed && !options.some((o) => o.value === typed) ? typed : null;

  function pick(next: string) {
    onChange(next);
    close();
  }

  return (
    <>
      <label className="crm-msearch">
        <Search className="size-4 shrink-0 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (CARET_KEYS.has(e.key)) {
              e.stopPropagation();
              return;
            }
            // Enter from the field takes the obvious answer: the one row left,
            // or the typed value. Anything else and the arrow keys are the way
            // in - guessing between six matches would be a lottery.
            if (e.key !== "Enter") return;
            e.preventDefault();
            if (custom) pick(custom);
            else if (shown.length === 1 && shown[0]) pick(shown[0].value);
          }}
          placeholder={searchPlaceholder}
          autoComplete="off"
          aria-label={searchPlaceholder}
        />
      </label>

      {custom ? (
        <button type="button" className="crm-mi" onClick={() => pick(custom)}>
          <CornerDownLeft className="size-4" />
          <span className="crm-trunc flex-1">
            {(customLabel ?? '"{value}"').replace("{value}", custom)}
          </span>
        </button>
      ) : null}

      {shown.length === 0 && !custom ? (
        <div className="crm-pal-empty">{emptyLabel ?? ""}</div>
      ) : null}

      {shown.map((option, i) => (
        <PickerRow
          key={option.value}
          option={option}
          // The heading is drawn by the first row of its run rather than by a
          // second pass over the list: the filter can empty a whole group, and
          // a heading rendered from the original order would then stand over
          // somebody else's voices.
          heading={option.group && option.group !== shown[i - 1]?.group ? option.group : null}
          chosen={option.value === value}
          mono={mono}
          onPick={pick}
        />
      ))}
    </>
  );
}

function PickerRow({
  option,
  heading,
  chosen,
  mono,
  onPick,
}: {
  option: PickerOption;
  heading: string | null;
  chosen: boolean;
  mono?: boolean;
  onPick: (value: string) => void;
}) {
  return (
    <>
      {heading ? <div className="crm-msect">{heading}</div> : null}
      <button
        type="button"
        role="option"
        aria-selected={chosen}
        className={cn("crm-mi", chosen && "is-on")}
        onClick={() => onPick(option.value)}
      >
        {option.icon}
        <span className="min-w-0 flex-1">
          <span className={cn("crm-trunc block", mono && "font-mono text-[12.25px]")}>
            {option.label}
          </span>
          {option.hint ? <span className="crm-mi-sub crm-trunc block">{option.hint}</span> : null}
        </span>
        {chosen ? <Check className="size-4 shrink-0" /> : null}
      </button>
    </>
  );
}
